import { Injectable } from '@angular/core';
import { ApiService } from '../../services';
import { CONTROLLERS, ENDPOINTS } from '../../constants';

interface PageIntegrityAnomaly {
  type: 'unexpected-script' | 'inline-script' | 'missing-header' | 'changed-header';
  detail: string;
}

/**
 * v1 change-/tamper-detection check for PCI DSS Requirement 11.6.1. Called once
 * from AppComponent, for an already-authenticated session only - reuses the
 * same authenticated HttpClient path every other API call already uses,
 * rather than standing up a separate anonymous/rate-limited endpoint. That
 * means it does not cover the pre-login/login page; card entry itself only
 * ever happens after login, which is the actual 6.4.3/11.6.1 scope.
 *
 * Compares the actual <script> tags and HTTP response headers the browser
 * received against the known-good baseline this app's own CSP declares in
 * staticwebapp.config.json. Any script from an unexpected origin, any inline
 * script, or any missing/changed security header gets reported to
 * Controller-API, which logs it as an Error with an attached exception -
 * that routes straight through the existing EmailAlertSink.
 *
 * v1 scope: checks once per app load, not continuously. A live
 * MutationObserver would also catch tampering introduced mid-session, but
 * produces false positives from browser extensions/ad blockers injecting
 * their own scripts - deferred pending real signal from this v1.
 *
 * Keep EXPECTED_SCRIPT_ORIGINS and EXPECTED_HEADERS in sync with
 * staticwebapp.config.json whenever the CSP changes - see the Payment Script
 * Registry (PCI-643-SCR-2026-001), remediation item 5.
 */
@Injectable({ providedIn: 'root' })
export class PageIntegrityCheckerService {
  private static readonly EXPECTED_SCRIPT_ORIGINS = [
    window.location.origin,
    'https://gateway.clearent.net',
    'https://gateway-int.clearent.net',
    'https://www.google.com',
    'https://www.gstatic.com',
  ];

  private static readonly EXPECTED_HEADERS: Record<string, string> = {
    'x-frame-options': 'DENY',
    'content-security-policy':
      "frame-ancestors 'none'; script-src 'self' https://gateway.clearent.net https://gateway-int.clearent.net https://www.google.com/recaptcha/ https://www.gstatic.com/recaptcha/",
    'x-content-type-options': 'nosniff',
    'referrer-policy': 'strict-origin-when-cross-origin',
  };

  constructor(private apiService: ApiService) {}

  check(): void {
    const anomalies = this.checkScripts();

    this.checkHeaders()
      .then((headerAnomalies) => {
        const all = [...anomalies, ...headerAnomalies];
        if (all.length) {
          this.report(all);
        }
      })
      .catch(() => {
        // A failed same-origin fetch is not itself an integrity finding - don't report noise.
      });
  }

  private checkScripts(): PageIntegrityAnomaly[] {
    const anomalies: PageIntegrityAnomaly[] = [];

    Array.from(document.scripts).forEach((script) => {
      if (!script.src) {
        anomalies.push({
          type: 'inline-script',
          detail: (script.textContent || '').slice(0, 200),
        });
        return;
      }

      let origin: string;
      try {
        origin = new URL(script.src).origin;
      } catch {
        anomalies.push({ type: 'unexpected-script', detail: script.src });
        return;
      }

      if (!PageIntegrityCheckerService.EXPECTED_SCRIPT_ORIGINS.includes(origin)) {
        anomalies.push({ type: 'unexpected-script', detail: script.src });
      }
    });

    return anomalies;
  }

  private async checkHeaders(): Promise<PageIntegrityAnomaly[]> {
    const anomalies: PageIntegrityAnomaly[] = [];
    const response = await fetch(window.location.href, {
      method: 'GET',
      cache: 'no-store',
    });

    for (const [header, expected] of Object.entries(
      PageIntegrityCheckerService.EXPECTED_HEADERS
    )) {
      const actual = response.headers.get(header);
      if (actual !== expected) {
        anomalies.push({
          type: actual === null ? 'missing-header' : 'changed-header',
          detail: `${header}: expected "${expected}", got "${actual ?? '(missing)'}"`,
        });
      }
    }

    return anomalies;
  }

  private report(anomalies: PageIntegrityAnomaly[]): void {
    this.apiService
      .post(
        `${CONTROLLERS.SECURITY}/${ENDPOINTS.SECURITY.REPORT_PAGE_INTEGRITY_ISSUE}`,
        {
          url: window.location.href,
          userAgent: navigator.userAgent,
          anomalies,
        }
      )
      .subscribe({
        error: () => {
          // Reporting failure must never break the app - this is a monitoring
          // signal, not a user-facing feature.
        },
      });
  }
}
